import { useEffect, useMemo, useState } from 'react';
import { subscribeStudentBadgeItems, subscribeStudentBadgeBranchSummary } from '@services/registry';
import { useCampusBranches } from '@hooks/tenant/useCampusBranches';
import { STUDENT_BADGES_DEFAULT_BRANCH_ID } from '@constants/firestorePaths';

function mergeRowsByBranch(branchRowsMap) {
    const out = [];
    for (const list of Object.values(branchRowsMap)) {
        for (const r of list || []) {
            out.push(r);
        }
    }
    return out.sort((a, b) => {
        const y = (Number(b.year) || 0) - (Number(a.year) || 0);
        if (y !== 0) return y;
        const bn = (Number(b.badgeNumber) || 0) - (Number(a.badgeNumber) || 0);
        if (bn !== 0) return bn;
        return String(a.branchId || '').localeCompare(String(b.branchId || ''));
    });
}

/**
 * Live student badges for a tenant.
 * @param {string} tenantId
 * @param {string} [branchId] When set, only that campus; when omitted, all campuses merged (plus `__default__` when no campuses).
 */
export function useStudentBadges({ tenantId, branchId } = {}) {
    const { branches, loading: branchesLoading } = useCampusBranches({ tenantId });
    const [branchRowsMap, setBranchRowsMap] = useState({});
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    const branchIds = useMemo(() => {
        if (!tenantId) return [];
        if (branchId) return [branchId];
        const ids = branches.map((b) => b.id).filter(Boolean);
        if (ids.length) return ids;
        return [STUDENT_BADGES_DEFAULT_BRANCH_ID];
    }, [tenantId, branchId, branches]);

    useEffect(() => {
        if (!tenantId || !branchIds.length) {
            setBranchRowsMap({});
            setSummary(null);
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        setBranchRowsMap({});

        const unsubs = branchIds.map((bid) =>
            subscribeStudentBadgeItems({
                tenantId,
                branchId: bid,
                onNext: (list) => {
                    setBranchRowsMap((prev) => ({ ...prev, [bid]: list }));
                    setLoading(false);
                },
                onError: () => setLoading(false),
            })
        );

        let unsubSummary = () => {};
        if (branchId) {
            unsubSummary = subscribeStudentBadgeBranchSummary({
                tenantId,
                branchId,
                onNext: (doc) => setSummary(doc),
                onError: () => {},
            });
        } else {
            setSummary(null);
        }

        return () => {
            unsubs.forEach((u) => u());
            unsubSummary();
        };
    }, [tenantId, branchIds.join('|'), branchId]);

    const rows = useMemo(() => mergeRowsByBranch(branchRowsMap), [branchRowsMap]);

    const defaultBadgeId = useMemo(() => {
        if (!rows.length) return null;
        const y = new Date().getFullYear();
        const forYear = rows.filter((r) => Number(r.year) === y);
        const pool = forYear.length ? forYear : rows;
        const branchesInPool = new Set(pool.map((r) => r.branchId).filter(Boolean));
        if (branchesInPool.size > 1) return null;
        const sorted = [...pool].sort((a, b) => (Number(b.badgeNumber) || 0) - (Number(a.badgeNumber) || 0));
        return sorted[0]?.id ?? null;
    }, [rows]);

    const badgeFormOptions = useMemo(
        () =>
            rows.map((r) => ({
                value: r.id,
                title: (r.title || '').trim() || 'Untitled',
                badgeNumber: r.badgeNumber,
                year: r.year,
                branchId: r.branchId,
                assignedStudentCount: Number(r.assignedStudentCount) || 0,
            })),
        [rows]
    );

    const badgeRowById = useMemo(() => {
        const m = new Map();
        for (const r of rows) {
            m.set(r.id, r);
        }
        return m;
    }, [rows]);

    const nextBadgeNumberPreview = useMemo(() => (Number(summary?.lastBadgeNumber) || 0) + 1, [summary]);

    const badgeLabelById = useMemo(() => {
        const m = new Map();
        for (const r of rows) {
            const title = (r.title || '').trim() || 'Untitled';
            m.set(r.id, `${title} — Badge ${r.badgeNumber} | ${r.year}`);
        }
        return m;
    }, [rows]);

    const badgeYearDefault = useMemo(() => new Date().getFullYear(), []);

    return {
        badgeRows: rows,
        summary,
        loading: loading || (!!tenantId && !branchId && branchesLoading),
        defaultBadgeId,
        badgeFormOptions,
        badgeRowById,
        nextBadgeNumberPreview,
        badgeLabelById,
        badgeYearDefault,
    };
}
